import React, { useEffect, useState } from 'react';
import { Box, Button, Heading, Layer, Text, TextInput } from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import { API } from '../../src/api';
import QueryString from 'qs';

export const NewMatchButton = ({
    isMatchForSingleContact = true,
    contactIds = [],
    revalidate,
    setIsToastVisible,
    setToastMessage,
}) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <Box>
            <Button
                primary
                size="large"
                label="Add job match"
                disabled={!isMatchForSingleContact && !contactIds.length > 0}
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <NewMatchModal
                    contactIds={contactIds}
                    revalidate={revalidate}
                    setShowModal={setShowModal}
                    setIsToastVisible={setIsToastVisible}
                    setToastMessage={setToastMessage}
                />
            )}
        </Box>
    );
};

const NewMatchModal = ({
    contactIds,
    revalidate,
    setShowModal,
    setIsToastVisible,
    setToastMessage,
}) => {
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState();
    const [selectedJob, setSelectedJob] = useState();
    const [buttonBusy, setButtonBusy] = useState();

    const createCandidacy = async () => {
        try {
            setButtonBusy(true);
            const formattedContactIds = contactIds.map((x) => Number(x));
            const result = await API.post('/api/candidacies/multi', {
                data: {
                    contactIds: formattedContactIds,
                    jobId: selectedJob.id,
                },
            });

            const duplicateCandidaciesCount =
                result.data.data.attributes.numberOfExistingCandidacies;

            if (duplicateCandidaciesCount > 0) {
                setIsToastVisible(true);
                setToastMessage(
                    `${duplicateCandidaciesCount} ${duplicateCandidaciesCount > 1 ? 'matches' : 'match'} failed due to duplicates`,
                );
            }

            revalidate();
            setShowModal(false);
        } catch (error) {
            setButtonBusy(false);
            console.log(error);
        }
    };

    const handleSelect = (job) => {
        setSelectedJob(job);
    };

    useEffect(() => {
        if (!searchInput.length) {
            setSearchResults();
            return;
        }

        searchJobs(searchInput);
    }, [searchInput]);

    const searchJobs = async (str) => {
        const query = QueryString.stringify({
            filters: {
                $or: [
                    { title: { $containsi: str } },
                    { company: { name: { $containsi: str } } },
                ],
                status: {
                    $in: ['open', 'preparing', 'demo'],
                },
            },
            pagination: {
                limit: 10,
            },
            populate: ['company'],
        });

        try {
            const jobsResponse = await API.get(`/api/jobs?${query}`);
            const formattedJobs = jobsResponse.data.data.map((job) => {
                return {
                    label: `${job.attributes.title} [${job.attributes.company.data.attributes.name}]`,
                    value: {
                        id: job.id,
                        title: `${job.attributes.title} [${job.attributes.company.data.attributes.name}]`,
                    },
                };
            });

            setSearchResults(formattedJobs);
        } catch (error) {
            console.log(error);
            return [];
        }
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        Add new job match
                    </Heading>
                    <Box align="end">
                        <Close
                            onClick={() => {
                                setShowModal(false);
                            }}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>
                    <Box
                        width="medium"
                        align="center"
                        round="6px"
                        background="white">
                        <TextInput
                            dropHeight="medium"
                            onChange={(event) =>
                                setSearchInput(event.target.value)
                            }
                            onSuggestionSelect={(event) =>
                                handleSelect(event.suggestion.value)
                            }
                            placeholder="Search"
                            suggestions={searchResults || []}
                            value={searchInput}
                        />
                    </Box>
                </Box>
                <Box>
                    {selectedJob && (
                        <Box direction="row" gap="small">
                            <Text>{selectedJob.title}</Text>
                            <Box align="end" justify="center">
                                <Close
                                    size="medium"
                                    color="red"
                                    onClick={() => {
                                        setSelectedJob(null);
                                    }}
                                    cursor="pointer"
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box>
                    <Button
                        primary
                        busy={buttonBusy}
                        disabled={!selectedJob}
                        label="Create job match"
                        style={{ width: '220px', height: '50px' }}
                        alignSelf="center"
                        onClick={createCandidacy}
                    />
                </Box>
            </Box>
        </Layer>
    );
};

NewMatchButton.propTypes = {
    isMatchForSingleContact: PropTypes.bool,
    contactIds: PropTypes.array,
    revalidate: PropTypes.func,
    setIsToastVisible: PropTypes.func,
    setToastMessage: PropTypes.func,
};

NewMatchModal.propTypes = {
    contactIds: PropTypes.array,
    revalidate: PropTypes.func,
    setShowModal: PropTypes.func,
    setIsToastVisible: PropTypes.func,
    setToastMessage: PropTypes.func,
};
